import React, { useState, useEffect, useCallback } from 'react';
import VerificationQueueTab from "../components/VerificationQueueTab";
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { useTranslation } from '../i18n';

const cardStyle = "bg-card rounded-lg border border-border p-5";
const statCardStyle = "bg-card rounded-lg border border-border p-4 text-center";
const tableHeaderStyle = "text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-4 py-3";
const tableCellStyle = "px-4 py-3 text-sm text-foreground/80";
const buttonPrimary = "bg-foreground text-white px-3 py-1.5 rounded text-sm hover:bg-foreground/80 transition";
const buttonSecondary = "bg-muted text-foreground/80 px-3 py-1.5 rounded text-sm hover:bg-muted transition border border-border";
const buttonDanger = "bg-red-50 text-red-700 px-3 py-1.5 rounded text-sm hover:bg-red-100 transition border border-red-200";
const inputStyle = "border border-border rounded px-3 py-1.5 text-sm w-full focus:outline-none focus:ring-1 focus:ring-gray-400";
const badgeBase = "inline-block px-2 py-0.5 rounded text-xs font-medium";

function PlanBadge({ plan }) {
  const colors = {
    pro: "bg-green-50 text-green-700 border border-green-200",
    basic: "bg-muted/50 text-muted-foreground border border-border",
    free: "bg-muted/50 text-muted-foreground border border-border",
  };
  return <span className={`${badgeBase} ${colors[plan] || colors.basic}`}>{plan || "basic"}</span>;
}

function RoleBadge({ role }) {
  const colors = {
    business: "bg-blue-50 text-blue-700",
    individual: "bg-purple-50 text-purple-700",
    seller: "bg-amber-50 text-amber-700",
    buyer: "bg-teal-50 text-teal-700",
  };
  return <span className={`${badgeBase} ${colors[role] || "bg-muted/50 text-muted-foreground"}`}>{role || "unknown"}</span>;
}

function StatCard({ label, value, sub }) {

  const fetchFeedback = async () => {
    try {
      const params = new URLSearchParams();
      if (feedbackFilter.type) params.set('type', feedbackFilter.type);
      if (feedbackFilter.status) params.set('status', feedbackFilter.status);
      const [list, stats] = await Promise.all([
        api.get(`/feedback?${params.toString()}`),
        api.get('/feedback/stats'),
      ]);
      setFeedbackList(list.feedback || []);
      setFeedbackStats(stats);
    } catch (e) { console.error('Feedback fetch error:', e); }
  };

  const updateFeedbackStatus = async (id, status) => {
    try {
      await api.patch(`/feedback/${id}`, { status });
      fetchFeedback();
    } catch (e) { console.error(e); }
  };

  const deleteFeedback = async (id) => {
    if (!window.confirm('Delete this feedback?')) return;
    try {
      await api.delete(`/feedback/${id}`);
      fetchFeedback();
    } catch (e) { console.error(e); }
  };

  return (
    <div className={statCardStyle}>
      <p className="text-2xl font-semibold text-foreground">{value}</p>
      <p className="text-xs text-muted-foreground mt-1">{label}</p>
      {sub && <p className="text-xs text-muted-foreground/60 mt-0.5">{sub}</p>}
    </div>
  );
}

function StatusDot({ status }) {
  const color = status === 'ok' || status === 'healthy' ? 'bg-green-400' : status === 'degraded' ? 'bg-yellow-400' : 'bg-red-400';
  return <span className={`inline-block w-2 h-2 rounded-full ${color} mr-1.5`}></span>;
}

const activityTypeStyles = {
  signup: "border-l-blue-400",
  deal: "border-l-amber-400",
  contract: "border-l-green-400",
  signature: "border-l-purple-400",
  document: "border-l-gray-400",
};

const activityTypeLabels = {
  signup: "Signup",
  deal: "Deal",
  contract: "Contract",
  signature: "Signature",
  document: "Document",
};

export default function SuperAdminPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [feedbackList, setFeedbackList] = useState([]);
  const [feedbackStats, setFeedbackStats] = useState(null);
  const [feedbackFilter, setFeedbackFilter] = useState({ type: "", status: "" });
  const [tab, setTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [orgs, setOrgs] = useState([]);
  const [orgsTotal, setOrgsTotal] = useState(0);
  const [userSearch, setUserSearch] = useState('');
  const [orgSearch, setOrgSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [trialModal, setTrialModal] = useState(null);
  const [trialDays, setTrialDays] = useState(30);
  const [activity, setActivity] = useState([]);
  const [health, setHealth] = useState(null);
  const [announcement, setAnnouncement] = useState({ active: false, message: '', type: 'info' });
  const [flags, setFlags] = useState([]);
  const [newFlagName, setNewFlagName] = useState('');
  const [newFlagDesc, setNewFlagDesc] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [emailLogs, setEmailLogs] = useState([]);
  const [emailLogsTotal, setEmailLogsTotal] = useState(0);

  const fetchStats = async () => {
    try {
      const data = await api.get('/superadmin/stats');
      setStats(data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Access denied');
    }
  };

  const fetchUsers = useCallback(async (search = '') => {
    setLoading(true);
    try {
      const params = search ? `?search=${encodeURIComponent(search)}&limit=50` : '?limit=50';
      const data = await api.get('/superadmin/users' + params);
      setUsers(data.users);
      setUsersTotal(data.total);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load users');
    }
    setLoading(false);
  }, []);

  const fetchOrgs = useCallback(async (search = '') => {
    setLoading(true);
    try {
      const params = search ? `?search=${encodeURIComponent(search)}&limit=50` : '?limit=50';
      const data = await api.get('/superadmin/organizations' + params);
      setOrgs(data.organizations);
      setOrgsTotal(data.total);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load organizations');
    }
    setLoading(false);
  }, []);

  const fetchActivity = async () => {
    try {
      const data = await api.get('/superadmin/activity?limit=50');
      setActivity(data.activities);
    } catch (e) { console.error(e); }
  };

  const fetchHealth = async () => {
    try {
      const data = await api.get('/superadmin/health');
      setHealth(data);
    } catch (e) { console.error(e); }
  };

  const fetchAnnouncement = async () => {
    try {
      const data = await api.get('/superadmin/announcements');
      setAnnouncement(data);
    } catch (e) { console.error(e); }
  };

  const saveAnnouncement = async () => {
    try {
      await api.put('/superadmin/announcements', announcement);
    } catch (e) { alert('Failed to save announcement'); }
  };

  const fetchFlags = async () => {
    try {
      const data = await api.get('/superadmin/feature-flags');
      setFlags(data.flags);
    } catch (e) { console.error(e); }
  };

  const toggleFlag = async (name, current) => {
    try {
      await api.put(`/superadmin/feature-flags/${name}`, { enabled: !current });
      fetchFlags();
    } catch (e) { alert('Failed to toggle flag'); }
  };

  const addFlag = async () => {
    if (!newFlagName.trim()) return;
    try {
      await api.put(`/superadmin/feature-flags/${newFlagName.trim()}`, { enabled: false, description: newFlagDesc });
      setNewFlagName('');
      setNewFlagDesc('');
      fetchFlags();
    } catch (e) { alert('Failed to add flag'); }
  };

  const deleteFlag = async (name) => {
    if (!window.confirm(`Delete flag "${name}"?`)) return;
    try {
      await api.delete(`/superadmin/feature-flags/${name}`);
      fetchFlags();
    } catch (e) { alert('Failed to delete flag'); }
  };

  const grantTrial = async (orgId) => {
    try {
      await api.post(`/superadmin/organizations/${orgId}/grant-trial`, { days: trialDays });
      setTrialModal(null);
      fetchOrgs(orgSearch);
      fetchStats();
    } catch (e) { alert(e.response?.data?.detail || 'Failed to grant trial'); }
  };

  const changePlan = async (orgId, plan) => {
    try {
      await api.patch(`/superadmin/organizations/${orgId}/plan`, { plan });
      fetchOrgs(orgSearch);
      fetchStats();
    } catch (e) { alert(e.response?.data?.detail || 'Failed to change plan'); }
  };

  const toggleBeta = async (orgId) => {
    try {
      await api.patch(`/superadmin/organizations/${orgId}/beta`);
      fetchOrgs(orgSearch);
    } catch (e) { alert(e.response?.data?.detail || 'Failed to toggle beta'); }
  };

  const toggleUser = async (userId) => {
    try {
      await api.patch(`/superadmin/users/${userId}/disable`);
      fetchUsers(userSearch);
    } catch (e) { alert(e.response?.data?.detail || 'Failed to toggle user'); }
  };

  const fetchUserDetail = async (userId) => {
    try {
      const data = await api.get(`/superadmin/users/${userId}`);
      setUserDetail(data);
      setSelectedUser(userId);
    } catch (e) { alert('Failed to load user detail'); }
  };

    const deleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to permanently delete this user? They will be able to sign up again with the same email. This cannot be undone.')) return;
    try {
      await api.delete('/superadmin/users/' + userId);
      setSelectedUser(null);
      setUserDetail(null);
      fetchUsers(userSearch);
      fetchStats();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to delete user');
    }
  };

  const fetchEmailLogs = async () => {
    try {
      const data = await api.get('/superadmin/email-logs?limit=50');
      setEmailLogs(data.logs || []);
      setEmailLogsTotal(data.total || 0);
    } catch (e) { console.error(e); }
  };

  const exportCSV = (type) => {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    const baseUrl = (window._env_?.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL || "");
    window.open(baseUrl + "/api/superadmin/export/" + type + "?token=" + token, "_blank");
  };

  const impersonateUser = async (userId, userName) => {
    if (!window.confirm("Impersonate  + userName + ? You will be logged in as them in a new tab.")) return;
    try {
      const data = await api.post('/superadmin/impersonate/' + userId);
      const url = window.location.origin + "/dashboard?impersonate_token=" + data.token;
      window.open(url, "_blank");
    } catch (e) { alert("Failed to impersonate"); }
  };

  useEffect(() => { fetchStats(); }, []);

  useEffect(() => {
    if (tab === 'users') fetchUsers(userSearch);
    if (tab === 'organizations') fetchOrgs(orgSearch);
    if (tab === 'activity') fetchActivity();
    if (tab === 'system') { fetchHealth(); fetchAnnouncement(); fetchFlags(); }
    if (tab === 'feedback') { fetchFeedback(); }
  }, [tab, fetchUsers, fetchOrgs, userSearch, orgSearch]);

  if (error === 'Access denied' || error === 'Super admin access required') {
  
  const fetchFeedback = async () => {
    try {
      const params = new URLSearchParams();
      if (feedbackFilter.type) params.set('type', feedbackFilter.type);
      if (feedbackFilter.status) params.set('status', feedbackFilter.status);
      const [list, stats] = await Promise.all([
        api.get(`/feedback?${params.toString()}`),
        api.get('/feedback/stats'),
      ]);
      setFeedbackList(list.feedback || []);
      setFeedbackStats(stats);
    } catch (e) { console.error('Feedback fetch error:', e); }
  };

  const updateFeedbackStatus = async (id, status) => {
    try {
      await api.patch(`/feedback/${id}`, { status });
      fetchFeedback();
    } catch (e) { console.error(e); }
  };

  const deleteFeedback = async (id) => {
    if (!window.confirm('Delete this feedback?')) return;
    try {
      await api.delete(`/feedback/${id}`);
      fetchFeedback();
    } catch (e) { console.error(e); }
  };

  return (
      <div className="min-h-screen flex items-center justify-center bg-muted/50">
        <div className={cardStyle + " max-w-md text-center"}>
          <p className="text-lg font-medium text-foreground mb-2">Access Denied</p>
          <p className="text-sm text-muted-foreground">This page is restricted to platform administrators.</p>
        </div>
      </div>
    );
  }

  const formatDate = (d) => {
    if (!d) return '--';
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatTime = (d) => {
    if (!d) return '--';
    const dt = new Date(d);
    const now = new Date();
    const diff = now - dt;
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return Math.floor(diff / 60000) + 'm ago';
    if (diff < 86400000) return Math.floor(diff / 3600000) + 'h ago';
    if (diff < 604800000) return Math.floor(diff / 86400000) + 'd ago';
    return formatDate(d);
  };

  const tabStyle = (t) => `px-4 py-2 text-sm font-medium transition ${tab === t ? 'text-foreground border-b-2 border-foreground' : 'text-muted-foreground hover:text-foreground/80'}`;


  const fetchFeedback = async () => {
    try {
      const params = new URLSearchParams();
      if (feedbackFilter.type) params.set('type', feedbackFilter.type);
      if (feedbackFilter.status) params.set('status', feedbackFilter.status);
      const [list, stats] = await Promise.all([
        api.get(`/feedback?${params.toString()}`),
        api.get('/feedback/stats'),
      ]);
      setFeedbackList(list.feedback || []);
      setFeedbackStats(stats);
    } catch (e) { console.error('Feedback fetch error:', e); }
  };

  const updateFeedbackStatus = async (id, status) => {
    try {
      await api.patch(`/feedback/${id}`, { status });
      fetchFeedback();
    } catch (e) { console.error(e); }
  };

  const deleteFeedback = async (id) => {
    if (!window.confirm('Delete this feedback?')) return;
    try {
      await api.delete(`/feedback/${id}`);
      fetchFeedback();
    } catch (e) { console.error(e); }
  };

  return (
    <div className="min-h-screen bg-muted/50">
      {/* Header */}
      <div className="bg-card border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-lg font-semibold text-foreground">Platform Admin</h1>
            <p className="text-xs text-muted-foreground">PocketFiler Super Admin Panel</p>
          </div>
          <div className="flex items-center gap-4">
            {health && (
              <div className="flex items-center text-xs text-muted-foreground">
                <StatusDot status={health.status} />
                {health.status}
              </div>
            )}
            <span className="text-xs text-muted-foreground/60">{user?.email}</span>
          </div>
        </div>
        {/* Tabs */}
        <div className="max-w-7xl mx-auto px-6 flex gap-6 border-t border-border">
          <button className={tabStyle('dashboard')} onClick={() => setTab('dashboard')}>Dashboard</button>
          <button className={tabStyle('users')} onClick={() => setTab('users')}>Users</button>
          <button className={tabStyle('organizations')} onClick={() => setTab('organizations')}>Organizations</button>
          <button className={tabStyle('activity')} onClick={() => setTab('activity')}>Activity</button>
          <button className={tabStyle("system")} onClick={() => setTab("system")}>System</button>
          <button className={tabStyle("verification")} onClick={() => setTab("verification")}>Verification</button>
          <button className={tabStyle("feedback")} onClick={() => setTab("feedback")}>Feedback</button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">

        {/* DASHBOARD TAB */}
        {tab === 'dashboard' && stats && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
              <StatCard label="Users" value={stats.totals.users} />
              <StatCard label="Organizations" value={stats.totals.organizations} />
              <StatCard label="Deals" value={stats.totals.deals} />
              <StatCard label="Contracts" value={stats.totals.contracts} />
              <StatCard label="Signatures" value={stats.totals.signatures} />
              <StatCard label="Documents" value={stats.totals.documents} />
              <StatCard label="Messages" value={stats.totals.messages} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className={cardStyle}>
                <h3 className="text-sm font-medium text-foreground/80 mb-3">Signups</h3>
                <div className="space-y-2">
                  <div className="flex justify-between"><span className="text-sm text-muted-foreground">Today</span><span className="text-sm font-medium">{stats.signups.today}</span></div>
                  <div className="flex justify-between"><span className="text-sm text-muted-foreground">This Week</span><span className="text-sm font-medium">{stats.signups.this_week}</span></div>
                  <div className="flex justify-between"><span className="text-sm text-muted-foreground">This Month</span><span className="text-sm font-medium">{stats.signups.this_month}</span></div>
                </div>
              </div>
              <div className={cardStyle}>
                <h3 className="text-sm font-medium text-foreground/80 mb-3">Deals</h3>
                <div className="space-y-2">
                  <div className="flex justify-between"><span className="text-sm text-muted-foreground">Today</span><span className="text-sm font-medium">{stats.deals_activity.today}</span></div>
                  <div className="flex justify-between"><span className="text-sm text-muted-foreground">This Week</span><span className="text-sm font-medium">{stats.deals_activity.this_week}</span></div>
                  <div className="flex justify-between"><span className="text-sm text-muted-foreground">This Month</span><span className="text-sm font-medium">{stats.deals_activity.this_month}</span></div>
                </div>
              </div>
              <div className={cardStyle}>
                <h3 className="text-sm font-medium text-foreground/80 mb-3">Contracts</h3>
                <div className="space-y-2">
                  <div className="flex justify-between"><span className="text-sm text-muted-foreground">Today</span><span className="text-sm font-medium">{stats.contracts_activity.today}</span></div>
                  <div className="flex justify-between"><span className="text-sm text-muted-foreground">This Week</span><span className="text-sm font-medium">{stats.contracts_activity.this_week}</span></div>
                  <div className="flex justify-between"><span className="text-sm text-muted-foreground">This Month</span><span className="text-sm font-medium">{stats.contracts_activity.this_month}</span></div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className={cardStyle}>
                <h3 className="text-sm font-medium text-foreground/80 mb-3">Plans</h3>
                <div className="space-y-2">
                  {Object.entries(stats.plans).map(([plan, count]) => (
                    <div key={plan} className="flex justify-between items-center">
                      <PlanBadge plan={plan} />
                      <span className="text-sm font-medium">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className={cardStyle}>
                <h3 className="text-sm font-medium text-foreground/80 mb-3">Roles</h3>
                <div className="space-y-2">
                  {Object.entries(stats.roles).map(([role, count]) => (
                    <div key={role} className="flex justify-between items-center">
                      <RoleBadge role={role} />
                      <span className="text-sm font-medium">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className={cardStyle}>
                <h3 className="text-sm font-medium text-foreground/80 mb-3">Trials</h3>
                <div className="space-y-2">
                  <div className="flex justify-between"><span className="text-sm text-muted-foreground">Active Trials</span><span className="text-sm font-medium">{stats.trials.active}</span></div>
                  <div className="flex justify-between"><span className="text-sm text-muted-foreground">Expired Trials</span><span className="text-sm font-medium">{stats.trials.expired}</span></div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* USERS TAB */}
        {tab === 'users' && (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <input type="text" placeholder="Search by name or email..." value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && fetchUsers(userSearch)}
                className={inputStyle + " max-w-sm"} />
              <button onClick={() => fetchUsers(userSearch)} className={buttonSecondary}>Search</button>
              <button onClick={() => exportCSV(users)} className={buttonSecondary}>Export CSV</button>
              <span className="text-xs text-muted-foreground/60 ml-auto">{usersTotal} total</span>
            </div>
            <div className="bg-card rounded-lg border border-border overflow-hidden">
              <table className="w-full">
                <thead className="bg-muted/50 border-b border-border">
                  <tr>
                    <th className={tableHeaderStyle}>Name</th>
                    <th className={tableHeaderStyle}>Email</th>
                    <th className={tableHeaderStyle}>Role</th>
                    <th className={tableHeaderStyle}>Organization</th>
                    <th className={tableHeaderStyle}>Plan</th>
                    <th className={tableHeaderStyle}>Joined</th>
                    <th className={tableHeaderStyle}>Status</th>
                    <th className={tableHeaderStyle}>Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {users.map(u => (
                    <tr key={u.id} className="hover:bg-muted/50 cursor-pointer" onClick={() => fetchUserDetail(u.id)}>
                      <td className={tableCellStyle + " font-medium"}>{u.name}</td>
                      <td className={tableCellStyle}>{u.email}</td>
                      <td className={tableCellStyle}><RoleBadge role={u.role} /></td>
                      <td className={tableCellStyle + " text-xs"}>{u.organization_name || '--'}</td>
                      <td className={tableCellStyle}><PlanBadge plan={u.plan} /></td>
                      <td className={tableCellStyle + " text-xs text-muted-foreground"}>{formatDate(u.created_at)}</td>
                      <td className={tableCellStyle}>
                        {u.disabled
                          ? <span className={`${badgeBase} bg-red-50 text-red-600`}>Disabled</span>
                          : <span className={`${badgeBase} bg-green-50 text-green-600`}>Active</span>}
                      </td>
                      <td className={tableCellStyle} onClick={e => e.stopPropagation()}>
                        <button onClick={() => toggleUser(u.id)} className="text-xs text-muted-foreground hover:text-foreground">
                          {u.disabled ? 'Enable' : 'Disable'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {loading && <div className="p-8 text-center text-sm text-muted-foreground/60">Loading...</div>}
              {!loading && users.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground/60">No users found</div>}
            </div>

            {/* User Detail Drawer */}
            {selectedUser && userDetail && (
              <div className="fixed inset-0 bg-black/30 flex justify-end z-50" onClick={() => setSelectedUser(null)}>
                <div className="bg-card w-full max-w-lg h-full overflow-y-auto shadow-xl" onClick={e => e.stopPropagation()}>
                  <div className="border-b border-border px-6 py-4 flex items-center justify-between">
                    <h2 className="text-sm font-semibold text-foreground">User Detail</h2>
                    <button onClick={() => setSelectedUser(null)} className="text-muted-foreground/60 hover:text-muted-foreground text-lg">x</button>
                  </div>
                  <div className="px-6 py-4 space-y-5">
                    <div className="space-y-3">
                      <div>
                        <p className="text-xs text-muted-foreground">Name</p>
                        <p className="text-sm font-medium">{userDetail.name || '--'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Email</p>
                        <p className="text-sm">{userDetail.email}</p>
                      </div>
                      <div className="flex gap-6">
                        <div>
                          <p className="text-xs text-muted-foreground">Role</p>
                          <RoleBadge role={userDetail.role} />
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Status</p>
                          {userDetail.disabled
                            ? <span className={`${badgeBase} bg-red-50 text-red-600`}>Disabled</span>
                            : <span className={`${badgeBase} bg-green-50 text-green-600`}>Active</span>}
                        </div>
                      </div>
                      <div className="flex gap-6">
                        <div>
                          <p className="text-xs text-muted-foreground">Joined</p>
                          <p className="text-sm">{formatDate(userDetail.created_at)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Last Login</p>
                          <p className="text-sm">{formatDate(userDetail.last_login)}</p>
                        </div>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">User ID</p>
                        <p className="text-xs font-mono text-muted-foreground/60">{userDetail._id}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Organization ID</p>
                        <p className="text-xs font-mono text-muted-foreground/60">{userDetail.organization_id || '--'}</p>
                      </div>
                    </div>

                    {userDetail.activity && (
                      <div className="border-t border-border pt-4">
                        <h3 className="text-xs font-medium text-foreground/80 mb-3">Activity (Organization)</h3>
                        <div className="grid grid-cols-3 gap-3">
                          <div className={statCardStyle}>
                            <p className="text-xl font-semibold">{userDetail.activity.deals}</p>
                            <p className="text-xs text-muted-foreground">Deals</p>
                          </div>
                          <div className={statCardStyle}>
                            <p className="text-xl font-semibold">{userDetail.activity.contracts}</p>
                            <p className="text-xs text-muted-foreground">Contracts</p>
                          </div>
                          <div className={statCardStyle}>
                            <p className="text-xl font-semibold">{userDetail.activity.documents}</p>
                            <p className="text-xs text-muted-foreground">Documents</p>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="border-t border-border pt-4 flex gap-2">
                      <button onClick={() => { toggleUser(userDetail._id); setSelectedUser(null); }}
                        className={userDetail.disabled ? buttonPrimary : buttonDanger}>
                        {userDetail.disabled ? 'Enable Account' : 'Disable Account'}
                      </button>
                      <button onClick={() => deleteUser(userDetail._id)}
                        className={buttonDanger}>
                        Delete User
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ORGANIZATIONS TAB */}
        {tab === 'organizations' && (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <input type="text" placeholder="Search organizations..." value={orgSearch}
                onChange={(e) => setOrgSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && fetchOrgs(orgSearch)}
                className={inputStyle + " max-w-sm"} />
              <button onClick={() => fetchOrgs(orgSearch)} className={buttonSecondary}>Search</button>
              <button onClick={() => exportCSV(organizations)} className={buttonSecondary}>Export CSV</button>
              <span className="text-xs text-muted-foreground/60 ml-auto">{orgsTotal} total</span>
            </div>
            <div className="bg-card rounded-lg border border-border overflow-hidden">
              <table className="w-full">
                <thead className="bg-muted/50 border-b border-border">
                  <tr>
                    <th className={tableHeaderStyle}>Organization</th>
                    <th className={tableHeaderStyle}>Plan</th>
                    <th className={tableHeaderStyle}>Members</th>
                    <th className={tableHeaderStyle}>Deals</th>
                    <th className={tableHeaderStyle}>Trial Ends</th>
                    <th className={tableHeaderStyle}>Beta</th>
                    <th className={tableHeaderStyle}>Created</th>
                    <th className={tableHeaderStyle}>Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {orgs.map(o => (
                    <tr key={o.id} className="hover:bg-muted/50">
                      <td className={tableCellStyle + " font-medium"}>{o.name}</td>
                      <td className={tableCellStyle}><PlanBadge plan={o.plan} /></td>
                      <td className={tableCellStyle + " text-center"}>{o.members}</td>
                      <td className={tableCellStyle + " text-center"}>{o.deals}</td>
                      <td className={tableCellStyle + " text-xs text-muted-foreground"}>{o.trial_ends_at ? formatDate(o.trial_ends_at) : '--'}</td>
                      <td className={tableCellStyle}>
                        {o.beta && <span className={`${badgeBase} bg-amber-50 text-amber-700`}>Beta</span>}
                      </td>
                      <td className={tableCellStyle + " text-xs text-muted-foreground"}>{formatDate(o.created_at)}</td>
                      <td className={tableCellStyle}>
                        <div className="flex items-center gap-2">
                          <button onClick={() => setTrialModal(o)} className="text-xs text-blue-600 hover:text-blue-800">Trial</button>
                          <select value={o.plan || 'free'} onChange={(e) => changePlan(o.id, e.target.value)}
                            className="text-xs border border-border rounded px-1 py-0.5">
                            <option value="free">Free</option>
                            <option value="pro">Pro</option>
                            <option value="starter">Small Business</option>
                            <option value="growth">Business Pro</option>
                            <option value="scale">Scale</option>
                            <option value="enterprise">Enterprise</option>
                          </select>
                          <button onClick={() => toggleBeta(o.id)} className="text-xs text-muted-foreground hover:text-foreground">
                            {o.beta ? 'Unbeta' : 'Beta'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {loading && <div className="p-8 text-center text-sm text-muted-foreground/60">Loading...</div>}
              {!loading && orgs.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground/60">No organizations found</div>}
            </div>
          </div>
        )}

        {/* ACTIVITY TAB */}
        {tab === 'activity' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-medium text-foreground/80">Recent Activity</h2>
              <button onClick={fetchActivity} className={buttonSecondary}>Refresh</button>
            </div>
            <div className="space-y-1">
              {activity.map((a, i) => (
                <div key={i} className={`bg-card border border-border rounded px-4 py-3 border-l-4 ${activityTypeStyles[a.type] || 'border-l-gray-300'} flex items-center justify-between`}>
                  <div className="flex items-center gap-3">
                    <span className={`${badgeBase} bg-muted/50 text-muted-foreground min-w-[70px] text-center`}>{activityTypeLabels[a.type] || a.type}</span>
                    <span className="text-sm text-foreground/80">{a.message}</span>
                  </div>
                  <span className="text-xs text-muted-foreground/60 whitespace-nowrap ml-4">{formatTime(a.timestamp)}</span>
                </div>
              ))}
              {activity.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground/60">No recent activity</div>}
            </div>
          </div>
        )}

        {/* SYSTEM TAB */}
        {tab === 'system' && (
          <div className="space-y-6">
            {/* Health */}
            {health && (
              <div className={cardStyle}>
                <h3 className="text-sm font-medium text-foreground/80 mb-3">System Health</h3>
                <div className="flex items-center gap-2 mb-4">
                  <StatusDot status={health.status} />
                  <span className="text-sm font-medium capitalize">{health.status}</span>
                </div>
                {health.checks && Object.entries(health.checks).map(([name, check]) => (
                  <div key={name} className="flex items-center justify-between py-1">
                    <div className="flex items-center gap-2">
                      <StatusDot status={check.status} />
                      <span className="text-sm text-foreground/80 capitalize">{name}</span>
                    </div>
                    {check.response_ms !== undefined && (
                      <span className="text-xs text-muted-foreground/60">{check.response_ms}ms</span>
                    )}
                  </div>
                ))}
                {health.collections && (
                  <div className="mt-4 border-t border-border pt-3">
                    <p className="text-xs text-muted-foreground mb-2">Collections ({Object.keys(health.collections).length})</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-1">
                      {Object.entries(health.collections).map(([name, count]) => (
                        <div key={name} className="flex justify-between text-xs px-2 py-1">
                          <span className="text-muted-foreground">{name}</span>
                          <span className="text-foreground/80 font-medium">{count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Announcement */}
            <div className={cardStyle}>
              <h3 className="text-sm font-medium text-foreground/80 mb-3">Announcement Banner</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={announcement.active}
                      onChange={(e) => setAnnouncement({...announcement, active: e.target.checked})}
                      className="rounded border-border" />
                    <span className="text-sm text-foreground/80">Active</span>
                  </label>
                  <select value={announcement.type}
                    onChange={(e) => setAnnouncement({...announcement, type: e.target.value})}
                    className="text-xs border border-border rounded px-2 py-1">
                    <option value="info">Info</option>
                    <option value="warning">Warning</option>
                    <option value="error">Error</option>
                  </select>
                </div>
                <input type="text" placeholder="Announcement message..."
                  value={announcement.message}
                  onChange={(e) => setAnnouncement({...announcement, message: e.target.value})}
                  className={inputStyle} />
                <button onClick={saveAnnouncement} className={buttonPrimary}>Save Announcement</button>
              </div>
            </div>

            {/* Feature Flags */}
            <div className={cardStyle}>
              <h3 className="text-sm font-medium text-foreground/80 mb-3">Feature Flags</h3>
              <div className="space-y-2 mb-4">
                {flags.map(f => (
                  <div key={f.name} className="flex items-center justify-between py-2 border-b border-border">
                    <div className="flex items-center gap-3">
                      <button onClick={() => toggleFlag(f.name, f.enabled)}
                        className={`w-10 h-5 rounded-full transition ${f.enabled ? 'bg-green-500' : 'bg-muted-foreground/30'} relative`}>
                        <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-card shadow transition ${f.enabled ? 'left-5' : 'left-0.5'}`}></span>
                      </button>
                      <div>
                        <span className="text-sm font-medium text-foreground/80">{f.name}</span>
                        {f.description && <p className="text-xs text-muted-foreground/60">{f.description}</p>}
                      </div>
                    </div>
                    <button onClick={() => deleteFlag(f.name)} className="text-xs text-red-400 hover:text-red-600">Remove</button>
                  </div>
                ))}
                {flags.length === 0 && <p className="text-xs text-muted-foreground/60">No feature flags configured</p>}
              </div>
              <div className="flex gap-2">
                <input type="text" placeholder="Flag name" value={newFlagName}
                  onChange={(e) => setNewFlagName(e.target.value)} className={inputStyle + " max-w-40"} />
                <input type="text" placeholder="Description (optional)" value={newFlagDesc}
                  onChange={(e) => setNewFlagDesc(e.target.value)} className={inputStyle + " max-w-60"} />
                <button onClick={addFlag} className={buttonSecondary}>Add Flag</button>
              </div>
            </div>
          </div>
        )}
      </div>


        {/* EMAIL LOGS TAB */}
        {tab === 'email-logs' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-medium text-foreground/80">Email Logs</h2>
              <div className="flex items-center gap-3">
                <span className="text-xs text-muted-foreground/60">{emailLogsTotal} total emails</span>
                <button onClick={fetchEmailLogs} className={buttonSecondary}>Refresh</button>
              </div>
            </div>
            <div className="bg-card rounded-lg border border-border overflow-hidden">
              <table className="w-full">
                <thead className="bg-muted/50 border-b border-border">
                  <tr>
                    <th className={tableHeaderStyle}>To</th>
                    <th className={tableHeaderStyle}>Subject</th>
                    <th className={tableHeaderStyle}>Type</th>
                    <th className={tableHeaderStyle}>Status</th>
                    <th className={tableHeaderStyle}>Sent</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {emailLogs.map((log, i) => (
                    <tr key={log._id || i} className="hover:bg-muted/50">
                      <td className={tableCellStyle}>{log.to}</td>
                      <td className={tableCellStyle + " max-w-xs truncate"}>{log.subject || "--"}</td>
                      <td className={tableCellStyle}><span className={badgeBase + " bg-muted/50 text-muted-foreground"}>{log.type || "--"}</span></td>
                      <td className={tableCellStyle}>
                        {log.status === "sent"
                          ? <span className={badgeBase + " bg-green-50 text-green-600"}>Sent</span>
                          : <span className={badgeBase + " bg-red-50 text-red-600"}>Failed</span>}
                      </td>
                      <td className={tableCellStyle + " text-xs text-muted-foreground"}>{formatTime(log.sent_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {emailLogs.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground/60">No emails logged yet</div>}
            </div>
          </div>
        )}

      {/* Trial Grant Modal */}
        {/* VERIFICATION TAB */}
        {tab === "verification" && <VerificationQueueTab api={api} formatDate={formatDate} tableHeaderStyle={tableHeaderStyle} tableCellStyle={tableCellStyle} cardStyle={cardStyle} inputStyle={inputStyle} buttonPrimary={buttonPrimary} buttonSecondary={buttonSecondary} />}

      
        {tab === 'feedback' && (
          <div className="space-y-4">
            {feedbackStats && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <div className={statCardStyle}><div className="text-2xl font-bold">{feedbackStats.total || 0}</div><div className="text-xs text-muted-foreground">Total</div></div>
                <div className={statCardStyle}><div className="text-2xl font-bold text-red-600">{feedbackStats.by_type?.bug || 0}</div><div className="text-xs text-muted-foreground">Bugs</div></div>
                <div className={statCardStyle}><div className="text-2xl font-bold text-amber-600">{feedbackStats.by_type?.feature || 0}</div><div className="text-xs text-muted-foreground">Features</div></div>
                <div className={statCardStyle}><div className="text-2xl font-bold text-green-600">{feedbackStats.by_status?.fixed || 0}</div><div className="text-xs text-muted-foreground">Fixed</div></div>
                <div className={statCardStyle}><div className="text-2xl font-bold text-blue-600">{feedbackStats.by_status?.open || 0}</div><div className="text-xs text-muted-foreground">Open</div></div>
              </div>
            )}
            <div className="flex gap-3 items-center">
              <select className={inputStyle + " !w-auto"} value={feedbackFilter.type} onChange={(e) => { setFeedbackFilter(f => ({...f, type: e.target.value})); setTimeout(fetchFeedback, 100); }}>
                <option value="">All Types</option>
                <option value="bug">Bugs</option>
                <option value="feature">Features</option>
                <option value="general">General</option>
              </select>
              <select className={inputStyle + " !w-auto"} value={feedbackFilter.status} onChange={(e) => { setFeedbackFilter(f => ({...f, status: e.target.value})); setTimeout(fetchFeedback, 100); }}>
                <option value="">All Status</option>
                <option value="open">Open</option>
                <option value="acknowledged">Acknowledged</option>
                <option value="in_progress">In Progress</option>
                <option value="fixed">Fixed</option>
                <option value="closed">Closed</option>
              </select>
              <button className={buttonSecondary} onClick={fetchFeedback}>Refresh</button>
            </div>
            <div className={cardStyle + " overflow-x-auto"}>
              <table className="w-full">
                <thead className="border-b"><tr>
                  <th className={tableHeaderStyle}>Type</th>
                  <th className={tableHeaderStyle}>Description</th>
                  <th className={tableHeaderStyle}>Page</th>
                  <th className={tableHeaderStyle}>User</th>
                  <th className={tableHeaderStyle}>Priority</th>
                  <th className={tableHeaderStyle}>Status</th>
                  <th className={tableHeaderStyle}>Date</th>
                  <th className={tableHeaderStyle}>Actions</th>
                </tr></thead>
                <tbody>
                  {feedbackList.map((fb) => (
                    <tr key={fb._id || fb.id} className="border-b hover:bg-muted/30">
                      <td className={tableCellStyle}>{fb.type === 'bug' ? '🐛' : fb.type === 'feature' ? '💡' : '💬'}</td>
                      <td className={tableCellStyle + " max-w-xs"}><div className="truncate" title={fb.description}>{fb.description}</div>
                        {fb.console_errors?.length > 0 && <div className="text-[10px] text-red-500 mt-0.5 truncate">{fb.console_errors[0]}</div>}
                      </td>
                      <td className={tableCellStyle}><span className="text-xs bg-muted px-1.5 py-0.5 rounded">{fb.page_url || '—'}</span></td>
                      <td className={tableCellStyle}><div className="text-xs">{fb.user_name || fb.user_email || '—'}</div></td>
                      <td className={tableCellStyle}><span className={badgeBase + ' ' + (fb.priority === 'high' ? 'bg-red-50 text-red-700' : fb.priority === 'medium' ? 'bg-amber-50 text-amber-700' : 'bg-green-50 text-green-700')}>{fb.priority}</span></td>
                      <td className={tableCellStyle}>
                        <select className="text-xs border rounded px-1.5 py-0.5" value={fb.status || 'open'} onChange={(e) => updateFeedbackStatus(fb._id || fb.id, e.target.value)}>
                          <option value="open">Open</option><option value="acknowledged">Acknowledged</option><option value="in_progress">In Progress</option>
                          <option value="fixed">Fixed</option><option value="closed">Closed</option>
                        </select>
                      </td>
                      <td className={tableCellStyle}><div className="text-xs">{fb.created_at ? new Date(fb.created_at).toLocaleDateString() : '—'}</div></td>
                      <td className={tableCellStyle}><button className="text-xs text-red-500 hover:text-red-700" onClick={() => deleteFeedback(fb._id || fb.id)}>Delete</button></td>
                    </tr>
                  ))}
                  {feedbackList.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-muted-foreground text-sm">No feedback yet</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {trialModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setTrialModal(null)}>
          <div className={cardStyle + " w-96"} onClick={e => e.stopPropagation()}>
            <h3 className="text-sm font-semibold text-foreground mb-4">Grant Pro Trial</h3>
            <p className="text-sm text-muted-foreground mb-3">{trialModal.name}</p>
            <div className="mb-4">
              <label className="text-xs text-muted-foreground block mb-1">Trial Duration (days)</label>
              <input type="number" value={trialDays} onChange={(e) => setTrialDays(Number(e.target.value))}
                className={inputStyle + " max-w-24"} min={1} max={365} />
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setTrialModal(null)} className={buttonSecondary}>Cancel</button>
              <button onClick={() => grantTrial(trialModal.id)} className={buttonPrimary}>Grant {trialDays}-Day Trial</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
